import { useEffect, useId, useRef, useState } from 'react';
import { EVENT_STATUSES, type EventStatus } from './labels';
import { useEscapeLayer } from '../ui/Dialog';

/**
 * What the calendar shows. `status` lives in the address (P13); "Tylko potwierdzone", closed events and rentals are
 * not remembered (owner remark 15, note 00:21, F28); the task layer is remembered per user (P7) by its owner.
 */
export type CalendarFilterValue = {
  status: EventStatus | '';
  onlyConfirmed: boolean;
  showClosed: boolean;
  showRentals: boolean;
  showTasks: boolean;
};
type Toggle = 'showClosed' | 'showRentals' | 'showTasks';

const TOGGLES: { key: Toggle; label: string; spoken: string; option?: 'rentals' | 'tasks' }[] = [
  { key: 'showClosed', label: 'Odrzucone/rezygnacje', spoken: 'odrzucone/rezygnacje' },
  { key: 'showRentals', label: 'Wypożyczenia', spoken: 'wypożyczenia', option: 'rentals' },
  { key: 'showTasks', label: 'Zadania firmowe', spoken: 'zadania firmowe', option: 'tasks' },
];
const ITEMS = '[role="menuitemradio"],[role="menuitemcheckbox"]';

function statusText(v: CalendarFilterValue): string {
  if (v.status) return EVENT_STATUSES[v.status];
  return v.onlyConfirmed ? 'Tylko potwierdzone' : 'Wszystkie statusy';
}

/**
 * One field for what the calendar shows (owner 20.10 23:37: "to do jakiejś strzałeczki ukryj albo do pola wyboru tak
 * jak wszystkie statusy"): it reads like the old status select ("Wszystkie statusy ▾", "+2" when extras are on) and
 * opens a short list — the statuses as a single choice (closes the list, like a select) and "Pokaż też" toggles
 * (stay open, ticked). Only the toggles the role can use (`options`). Keyboard as in a menu.
 */
export function CalendarFilter({ value, options, onChange }: { value: CalendarFilterValue; options: { rentals: boolean; tasks: boolean }; onChange: (v: CalendarFilterValue) => void }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const toggles = TOGGLES.filter((t) => !t.option || options[t.option]);
  const on = toggles.filter((t) => value[t.key]);
  const name = `Status i widok: ${statusText(value)}${on.length ? `; pokazane też: ${on.map((t) => t.spoken).join(', ')}` : ''}`;

  useEffect(() => {
    if (!open) return;
    (root.current?.querySelector<HTMLElement>('[aria-checked="true"]') ?? root.current?.querySelector<HTMLElement>(ITEMS))?.focus();
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // focus goes to the chosen status only when the list opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEscapeLayer(() => {
    setOpen(false);
    button.current?.focus();
  }, open);

  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>(ITEMS)];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') setOpen(false);
  };
  // focus going to an element around it is Safari pressing an option (it focuses that instead): not leaving (as RowMenu)
  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const to = e.relatedTarget;
    if (to instanceof Node && !root.current?.contains(to) && !to.contains(root.current)) setOpen(false);
  };
  const pick = (next: Partial<CalendarFilterValue>) => {
    onChange({ ...value, ...next });
    setOpen(false);
    button.current?.focus();
  };
  const radio = (label: string, checked: boolean, next: Partial<CalendarFilterValue>) => (
    <button key={label} type="button" role="menuitemradio" aria-checked={checked} onClick={() => pick(next)}>
      <Tick on={checked} />
      {label}
    </button>
  );

  return (
    <div className="cal-filter" ref={root}>
      <button
        ref={button}
        type="button"
        className="cal-filter-field"
        aria-label={name}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cal-filter-text">{statusText(value)}</span>
        {on.length > 0 && (
          <span className="cal-filter-count" title={`Pokazane też: ${on.map((t) => t.spoken).join(', ')}`}>
            +{on.length}
          </span>
        )}
        <svg className="cal-filter-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 4.5 6 7.5l3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="cal-filter-pop" role="menu" id={id} aria-label="Status i widok" onKeyDown={onMenuKey} onBlur={onBlur}>
          <div role="group" aria-label="Status">
            {radio('Wszystkie statusy', !value.status && !value.onlyConfirmed, { status: '', onlyConfirmed: false })}
            {radio('Tylko potwierdzone', !value.status && value.onlyConfirmed, { status: '', onlyConfirmed: true })}
            {(Object.keys(EVENT_STATUSES) as EventStatus[]).map((s) => radio(EVENT_STATUSES[s], value.status === s, { status: s, onlyConfirmed: false }))}
          </div>
          <div role="separator" className="cal-filter-sep" />
          <div role="group" aria-labelledby={`${id}-show`}>
            <p className="cal-filter-head" id={`${id}-show`}>
              Pokaż też
            </p>
            {toggles.map((t) => (
              <button key={t.key} type="button" role="menuitemcheckbox" aria-checked={value[t.key]} onClick={() => onChange({ ...value, [t.key]: !value[t.key] })}>
                <Box on={value[t.key]} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Tick({ on }: { on: boolean }) {
  return (
    <svg className="cal-filter-tick" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      {on && <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

/** A toggle's box in the tick column (a ticked box when on), so "Pokaż też" reads as switches, not as a choice. */
function Box({ on }: { on: boolean }) {
  return (
    <svg className="cal-filter-tick" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.2" />
      {on && <path d="M4.6 8.3l2.3 2.3 4.5-5" fill="none" stroke="var(--surface)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}
